import React, { useEffect } from 'react';
import { Square, Trash2 } from 'lucide-react';
import { CAP_WARNING_MS, MAX_RECORDING_MS, formatAudioDuration } from '../../utils/audio';
import type { VoiceRecorderPhase } from '../../hooks/useVoiceRecorder';

interface VoiceRecorderBarProps {
  // 'starting' while the browser asks for the microphone; 'encoding' between
  // stop and send.
  phase: Exclude<VoiceRecorderPhase, 'idle'>;
  durationMillis: number;
  // 0..1 from the recorder's analyser.
  level: number;
  onStop: () => void;
  onCancel: () => void;
}

const METER_BARS = 9;

// Replaces the composer's text row while a voice note is being recorded: a
// blinking dot, the timer, a level meter and the two controls. Escape discards.
const VoiceRecorderBar: React.FC<VoiceRecorderBarProps> = ({
  phase,
  durationMillis,
  level,
  onStop,
  onCancel,
}) => {
  const recording = phase === 'recording';
  const nearCap = recording && durationMillis >= MAX_RECORDING_MS - CAP_WARNING_MS;

  // Escape discards, unless something else on the page claimed the key: a
  // handler that consumed it (dialogs, the patient picker), a text control or
  // an overlay the event came from, or an open dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const target = e.target instanceof Element ? e.target : null;
      if (
        target &&
        target !== document.body &&
        target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="listbox"], [role="combobox"]')
      ) {
        return;
      }
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      e.preventDefault();
      onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const label =
    phase === 'starting' ? 'Esperando al micrófono…' : phase === 'encoding' ? 'Enviando…' : 'Grabando…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 38 }}>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Descartar grabación"
          title="Descartar grabación"
          style={{
            border: '1px solid var(--border)',
            borderRadius: 10,
            width: 38,
            height: 38,
            flexShrink: 0,
            background: 'var(--card)',
            color: 'var(--destructive)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Trash2 size={18} aria-hidden="true" />
        </button>

        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 12px',
            height: 38,
            borderRadius: 10,
            border: '1px solid var(--border)',
            background: 'var(--card)',
          }}
        >
          <span
            aria-hidden="true"
            className={recording ? 'voice-recording-dot' : undefined}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              flexShrink: 0,
              background: recording ? 'var(--destructive)' : 'var(--muted-foreground)',
            }}
          />
          {/* Only the phase is announced; a live timer would be read every second. */}
          <span role="status" aria-live="polite" style={{ fontSize: 13, color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
            {label}
          </span>
          <span
            aria-hidden="true"
            style={{ display: 'flex', alignItems: 'center', gap: 3, height: 20, flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            {Array.from({ length: METER_BARS }, (_, i) => {
              // Bell-shaped weights so the row reads as one voice, not nine.
              const centre = (METER_BARS - 1) / 2;
              const weight = 0.35 + 0.65 * Math.cos(((i - centre) / METER_BARS) * Math.PI);
              const scale = recording ? 0.2 + level * weight * 0.8 : 0.2;
              return (
                <span
                  key={i}
                  style={{
                    width: 3,
                    height: 20,
                    borderRadius: 2,
                    flexShrink: 0,
                    background: recording ? 'var(--primary)' : 'var(--border)',
                    // Computed geometry, the case docs/ui-guidelines.md allows.
                    transform: `scaleY(${scale.toFixed(2)})`,
                    transition: 'transform 120ms ease-out',
                  }}
                />
              );
            })}
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              color: nearCap ? 'var(--destructive)' : 'var(--foreground)',
              whiteSpace: 'nowrap',
            }}
          >
            {formatAudioDuration(durationMillis)}
          </span>
        </div>

        <button
          type="button"
          onClick={onStop}
          disabled={!recording}
          aria-label="Parar y enviar"
          title="Parar y enviar"
          style={{
            border: 'none',
            borderRadius: 10,
            height: 38,
            padding: '0 14px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: recording ? 'var(--destructive)' : 'var(--muted)',
            color: recording ? 'var(--card)' : 'var(--muted-foreground)',
            fontWeight: 600,
            fontSize: 14,
            cursor: recording ? 'pointer' : 'default',
          }}
        >
          <Square size={14} fill="currentColor" aria-hidden="true" />
          Parar y enviar
        </button>
      </div>
      {nearCap && (
        <div style={{ fontSize: 12, color: 'var(--destructive)' }}>Casi en el límite de dos minutos</div>
      )}
    </div>
  );
};

export default VoiceRecorderBar;
